import { Image } from 'expo-image';
import { useState } from 'react';
import { View, ViewProps } from 'react-native';

import type { PageBuilderWidgetHeroVideo } from '@fhs/cms-groq-queries';
import { imageUrlBuilder } from '@fhs/cms-groq-queries';
import { Text, YStack, createMqStyles, tokens } from '@fhs/ui';

import { AppStoreButton } from '../components/app-store-button';
import { CtaButton } from '../components/cta-button';
import { SimpleBlockContent } from '../components/simple-block-content';
import VimeoBackgroundVideo from '../components/vimeo-background-video';
import { ActionHandler } from '../types';
import { addActionSource } from '../utils/action';
import { plainTextFromPageBuilderSimpleBlockContent } from '../utils/plain-text-from-block-content';
import { webEyebrowTextRegionProps } from '../utils/web-eyebrow-text-a11y';

export type WidgetHeroVideoProps = Pick<
  PageBuilderWidgetHeroVideo,
  | 'video'
  | 'mediaAltText'
  | 'prefixType'
  | 'prefixImage'
  | 'prefixText'
  | 'title'
  | 'description'
  | 'buttons'
> & {
  onAction?: ActionHandler;
  style?: ViewProps['style'];
};

export function WidgetHeroVideo(props: WidgetHeroVideoProps) {
  const mqStyles = useMqStyles();
  const [layoutHeight, setLayoutHeight] = useState(0);
  const [layoutWidth, setLayoutWidth] = useState(0);
  const heroTitlePlain = plainTextFromPageBuilderSimpleBlockContent(props.title);
  const onAction: ActionHandler = props.onAction ?? (() => {});

  return (
    <View
      style={[mqStyles.widgetWrapper, props.style]}
      onLayout={e => {
        setLayoutHeight(e.nativeEvent.layout.height);
        setLayoutWidth(e.nativeEvent.layout.width);
      }}
    >
      <VimeoBackgroundVideo
        video={props.video}
        mediaAltText={props.mediaAltText}
        width={layoutWidth}
        height={layoutHeight}
      />

      <YStack style={mqStyles.contentArea}>
        <YStack style={mqStyles.textContentArea}>
          {(() => {
            switch (props.prefixType) {
              case 'none': {
                return null;
              }

              case 'image': {
                if (!props.prefixImage) {
                  return null;
                }

                const prefixImageLabel =
                  props.prefixText?.trim() ||
                  (heroTitlePlain ? `${heroTitlePlain} label` : '') ||
                  'Hero label';
                return (
                  <Image
                    source={imageUrlBuilder.image(props.prefixImage).format('webp').toString()}
                    accessibilityLabel={prefixImageLabel}
                    alt={prefixImageLabel}
                    style={mqStyles.prefixImage}
                    contentFit="contain"
                    contentPosition="left"
                  />
                );
              }

              case 'text': {
                const eyebrow = props.prefixText?.trim() ?? '';
                if (!eyebrow) {
                  return null;
                }
                return (
                  <View
                    accessible
                    accessibilityLabel={eyebrow}
                    accessibilityRole="text"
                    {...webEyebrowTextRegionProps(eyebrow)}
                  >
                    <Text.Paragraph
                      accessible={false}
                      importantForAccessibility="no"
                      size="md"
                      style={mqStyles.prefixText}
                    >
                      {props.prefixText}
                    </Text.Paragraph>
                  </View>
                );
              }
            }
          })()}

          <View style={mqStyles.textMaxWidth}>
            {props.title && (
              <SimpleBlockContent
                value={props.title}
                onAction={addActionSource({ componentType: 'block' }, onAction)}
                normalTextType="heading"
                normalText={{
                  type: 'superhero',
                  style: {
                    color: tokens.colors.$white,
                    textShadowOffset: { width: 0, height: 0 },
                    textShadowRadius: 16,
                    textShadowColor: tokens.colors.$blackOpacity75,
                  },
                }}
              />
            )}
          </View>

          <View style={mqStyles.textMaxWidth}>
            {props.description && (
              <SimpleBlockContent
                value={props.description}
                onAction={addActionSource(
                  { component: 'description', componentType: 'block' },
                  onAction
                )}
                normalTextType="heading"
                normalText={{
                  type: 'one',
                  style: {
                    color: tokens.colors.$white,
                    textShadowOffset: { width: 0, height: 0 },
                    textShadowRadius: 12,
                    textShadowColor: tokens.colors.$blackOpacity75,
                  },
                }}
              />
            )}
          </View>
        </YStack>

        <View style={mqStyles.buttons}>
          {(props.buttons ?? []).map(btn => {
            switch (btn._type) {
              case 'pageBuilderButtonCta': {
                return (
                  <CtaButton
                    key={btn._key}
                    onAction={addActionSource(
                      { component: 'CTA', componentType: 'button', text: btn.text },
                      onAction
                    )}
                    variant={btn.variant}
                    size="xl"
                    leadingIcon={btn.leadingIcon}
                    link={btn.link}
                    trailingIcon={btn.trailingIcon}
                    text={btn.text}
                  />
                );
              }

              case 'pageBuilderButtonStore': {
                return (
                  <AppStoreButton
                    key={btn._key}
                    platform={btn.platform}
                    onAction={addActionSource(
                      {
                        component: 'App Store',
                        componentType: 'button',
                        text: btn.platform,
                      },
                      onAction
                    )}
                  />
                );
              }

              default: {
                return null;
              }
            }
          })}
        </View>
      </YStack>
    </View>
  );
}

const useMqStyles = createMqStyles({
  widgetWrapper: {
    $base: {
      position: 'relative',
      maxWidth: 1440,
      width: '100%',
      marginInline: 'auto',
    },
    $gteDesktop: {
      height: 500,
    },
  },
  contentArea: {
    $base: {
      backgroundColor: tokens.colors.$blackOpacity10,
      flexGrow: 1,
      paddingVertical: 54,
      paddingHorizontal: 32,

      justifyContent: 'center',
      gap: 28,
    },
    $gteDesktop: {
      paddingHorizontal: 80,
      gap: 48,
    },
  },
  textContentArea: {
    $base: {
      gap: 4,
    },
    $gteDesktop: {
      gap: 20,
    },
  },
  prefixImage: {
    $base: {
      height: 32,
    },
    $gteDesktop: {
      height: 44,
    },
  },
  prefixText: {
    $base: {
      color: tokens.colors.$white,
    },
  },
  textMaxWidth: {
    $base: {
      maxWidth: 600,
    },
    $gteDesktop: {
      maxWidth: 860,
    },
  },
  buttons: {
    $base: {
      gap: 16,
    },
    $gteDesktop: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 24,
    },
  },
});
